<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useProjectionsStore } from '../stores/projections'

const LEAGUES = [
  { id: 'basketball-champions-league', name: 'Basketball Champions League' },
  { id: 'eurocup', name: 'EuroCup' },
  { id: 'euroleague', name: 'EuroLeague' },
  { id: 'fiba-europe-cup', name: 'FIBA Europe Cup' },
  { id: 'finland-korisliiga', name: 'Finland Korisliiga' },
  { id: 'france-betclic-elite', name: 'France Betclic Elite' },
  { id: 'germany-easycredit-bb', name: 'Germany EasyCredit BBL' },
  { id: 'greece-heba-a1', name: 'Greece HEBA A1' },
  { id: 'italy-lba-serie-a', name: 'Italy LBA Serie A' },
  { id: 'japan-b1-league', name: 'Japan B1 League' },
  { id: 'latvian-estonian-basketball-league', name: 'Latvian-Estonian Basketball League' },
  { id: 'latvian-estonian-basketball-league-po', name: 'Latvian-Estonian Basketball League PO' },
  { id: 'lithuania-betsafe-lkl', name: 'Lithuania Betsafe LKL' },
  { id: 'nba', name: 'NBA' },
  { id: 'poland-plk', name: 'Poland PLK' },
  { id: 'spain-liga-endesa', name: 'Spain Liga Endesa' },
  { id: 'sweden-basketligan', name: 'Sweden Basketligan' },
  { id: 'turkey-bsl', name: 'Turkey BSL' },
  { id: 'wnba', name: 'WNBA' },
  { id: 'world-cup', name: 'World Cup' },
  { id: 'eurobasket', name: 'EuroBasket' },
  { id: 'eurobasket-2025', name: 'EuroBasket 2025' },
  { id: 'world-cup-european-qualifiers', name: 'World Cup European Qualifiers' },
  { id: 'eurobasket-qualifying-rounds', name: 'EuroBasket Qualifying Rounds' },
  { id: 'olympic-games', name: 'Olympic Games' },
  { id: 'olympic-games-qualifying-tournament', name: 'Olympic Games Qualifying Tournament' },
  { id: 'basketball-champions-league-qualifying-rounds', name: 'Basketball Champions League Qualifying Rounds' },
]

const SEASONS = [
  '2019-2020',
  '2020-2021',
  '2021-2022',
  '2022-2023',
  '2023-2024',
  '2024-2025',
  '2025-2026'
]

const props = defineProps({
  initialLeague: {
    type: [String, Number],
    default: ''
  },
  initialSeason: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['filter-change'])

// Use projections store
const projectionsStore = useProjectionsStore()

// Get reactive references to store state
const selectedLeagues = computed({
  get: () => projectionsStore.getFilterLeagues,
  set: (value) => projectionsStore.setFilterLeagues(value)
})

const selectedSeasons = computed({
  get: () => projectionsStore.getFilterSeasons,
  set: (value) => projectionsStore.setFilterSeasons(value)
})

const isLeagueDropdownOpen = ref(false)
const isSeasonDropdownOpen = ref(false)

// Initialize filters in store
onMounted(() => {
  // Initialize filters if they're empty
  if (selectedLeagues.value.length === 0 && selectedSeasons.value.length === 0) {
    projectionsStore.initializeFilters()
  }
  
  // Emit initial state
  emit('filter-change', {
    leagues: selectedLeagues.value,
    seasons: selectedSeasons.value
  })
})

const toggleAllLeagues = () => {
  try {
    if (selectedLeagues.value.length === LEAGUES.length) {
      // If all are selected, deselect all
      projectionsStore.setFilterLeagues([])
    } else {
      // If not all are selected, select all
      projectionsStore.setFilterLeagues(LEAGUES.map(league => league.id))
    }
  } catch (error) {
    console.error('Error toggling all leagues:', error)
  }
}

const toggleLeague = (leagueId) => {
  try {
    const currentLeagues = [...selectedLeagues.value]
    const index = currentLeagues.indexOf(leagueId)
    if (index === -1) {
      currentLeagues.push(leagueId)
    } else {
      currentLeagues.splice(index, 1)
    }
    projectionsStore.setFilterLeagues(currentLeagues)
  } catch (error) {
    console.error('Error toggling league:', error)
  }
}

const toggleSeason = (season) => {
  try {
    const currentSeasons = [...selectedSeasons.value]
    const index = currentSeasons.indexOf(season)
    if (index === -1) {
      currentSeasons.push(season)
    } else {
      currentSeasons.splice(index, 1)
    }
    projectionsStore.setFilterSeasons(currentSeasons)
  } catch (error) {
    console.error('Error toggling season:', error)
  }
}

const getSelectedLeagueNames = () => {
  try {
    if (selectedLeagues.value.length === 0) return 'All Leagues'
    if (selectedLeagues.value.length === LEAGUES.length) return 'All Leagues'
    return selectedLeagues.value
      .map(id => LEAGUES.find(l => l.id === id)?.name)
      .filter(Boolean)
      .join(', ')
  } catch (error) {
    console.error('Error getting league names:', error)
    return 'All Leagues'
  }
}

const getSelectedSeasonText = () => {
  try {
    if (selectedSeasons.value.length === 0) return 'All Seasons'
    return selectedSeasons.value.join(', ')
  } catch (error) {
    console.error('Error getting season text:', error)
    return 'All Seasons'
  }
}

// Watch store filters and emit changes
watch(
  () => projectionsStore.getFilters,
  (newFilters) => {
    try {
      emit('filter-change', {
        leagues: newFilters.leagues,
        seasons: newFilters.seasons
      })
    } catch (error) {
      console.error('Error emitting filter change:', error)
    }
  },
  { deep: true }
)
</script>

<template>
  <!-- projections automatically generated -->

<div  class="flex gap-4 mb-6">
    <!-- Season Multi-Select -->
    <div 
    class="flex-1 relative"
    >
      <label class="block text-sm font-medium text-gray-700 mb-1">Seasons</label>
      <div
        @click="isSeasonDropdownOpen = !isSeasonDropdownOpen"
        class="w-full rounded-lg border border-gray-300 px-3 py-2 cursor-pointer bg-white hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <div class="flex justify-between items-center">
          <span class="truncate">{{ getSelectedSeasonText() }}</span>
          <i class="fas fa-chevron-down text-gray-400"></i>
        </div>
      </div>
      
      <!-- Season Dropdown -->
      <div
        v-if="isSeasonDropdownOpen"
        class="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto"
      >
        <div class="p-2">
          <div
            v-for="season in SEASONS"
            :key="season"
            @click="toggleSeason(season)"
            class="px-3 py-2 rounded-md cursor-pointer hover:bg-gray-100 flex items-center"
            :class="{ 'bg-blue-50': selectedSeasons.includes(season) }"
          >
            <div class="w-4 h-4 mr-2 border rounded flex items-center justify-center"
                 :class="{ 'bg-blue-500 border-blue-500': selectedSeasons.includes(season) }">
              <i v-if="selectedSeasons.includes(season)" class="fas fa-check text-white text-xs"></i>
            </div>
            {{ season }}
          </div>
        </div>
      </div>
    </div>
    <!-- League Multi-Select -->
    <div class="flex-1 relative min-w-[200px]">
      <label class="block text-sm font-medium text-gray-700 mb-1">Leagues</label>
      <div
        @click="isLeagueDropdownOpen = !isLeagueDropdownOpen"
        class="w-full rounded-lg border border-gray-300 px-3 py-2 cursor-pointer bg-white hover:border-blue-500 focus:outline-none"
      >
        <div class="flex justify-between items-center">
          <span class="truncate block w-full">{{ getSelectedLeagueNames() }}</span>
          <i class="fas fa-chevron-down text-gray-400 ml-2"></i>
        </div>
      </div>
      <!-- Dropdown Modal Overlay to avoid dropdown cutoff -->
      <div
        v-if="isLeagueDropdownOpen"
        class="fixed inset-0 z-30"
        @click="isLeagueDropdownOpen = false"
        aria-hidden="true"
        style="background: transparent;"
      ></div>
      <!-- League Dropdown -->
      <div
        v-if="isLeagueDropdownOpen"
        class="absolute z-40 left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 max-h-72 overflow-y-auto"
      >
        <!-- Toggle All Button -->
        <div class="sticky top-0 bg-white border-b border-gray-200 p-2 z-10">
          <button
            @click.stop="toggleAllLeagues"
            class="w-full px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-md flex items-center justify-center gap-2"
          >
            <i class="fas" :class="selectedLeagues.length === LEAGUES.length ? 'fa-check-square' : 'fa-square'"></i>
            <span>
              {{ selectedLeagues.length === LEAGUES.length ? 'Deselect All' : 'Select All' }}
            </span>
          </button>
        </div>
        <!-- League List -->
        <div class="p-2 divide-y divide-gray-100">
          <div
            v-for="league in LEAGUES"
            :key="league.id"
            @click.stop="toggleLeague(league.id)"
            class="flex items-center px-2 py-2 rounded cursor-pointer hover:bg-blue-50 group"
            :class="{ 'bg-blue-50': selectedLeagues.includes(league.id) }"
            style="transition: background .12s;"
          >
            <input
              type="checkbox"
              class="form-checkbox h-4 w-4 text-blue-500 border-blue-400 focus:ring-0 mr-3"
              :checked="selectedLeagues.includes(league.id)"
              @change.stop="toggleLeague(league.id)"
              tabindex="-1"
            />
            <span class="truncate text-[13px]" :class="selectedLeagues.includes(league.id) ? 'font-semibold text-blue-600' : 'text-gray-700'">
              {{ league.name }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: #CBD5E0 #EDF2F7;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #EDF2F7;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #CBD5E0;
  border-radius: 3px;
}
</style> 